import { filterPageTypes, filterStateSelector } from 'src/app/store/filters.era';
import { FilterGroup, IToggleFilter } from './../filters.era';
import { User } from 'globals/store/user.data';
import { Task, tasksStateSelector } from 'src/app/store/tasks.era';
import { createSelector } from '@ngrx/store';
import { Dictionary } from '@ngrx/entity';
import { sortByProp } from 'globals/helpers/helpers';
import { filteredCalendarEventsReselectorGenerator } from './filteredEvents.reselector';
import { eventPreferencesStateSelector } from '../event.preferences.era';
import { userStateSelector } from '../user.era';
import { eventStateSelector } from '../events.era';


const tasksDict = createSelector(tasksStateSelector, (state) => state.entities);
const sidebarTaskFilters = createSelector(filterStateSelector, (state) => state.tasksSidebar);
const sidebarToggleFilters = createSelector(filterStateSelector, (state) => state.toggleFilters.tasksSidebar);

const tableTaskFilters = createSelector(filterStateSelector, (state) => state.tasksTable);
const tableToggleFilters = createSelector(filterStateSelector, (state) => state.toggleFilters.tasksTable);

const calendarTaskFilters = createSelector(filterStateSelector, (state) => state.tasksCalendar);
const calendarToggleFilters = createSelector(filterStateSelector, (state) => state.toggleFilters.tasksCalendar);

const allTasksCalendarFilters = createSelector(filterStateSelector, (state) => state.allTasksCalendar);
const allTasksCalendarToggleFilters = createSelector(filterStateSelector, (state) => state.toggleFilters.allTasksCalendar);

const mainpageTaskFilters = createSelector(filterStateSelector, (state) => state.tasks);
const mainpageToggleFilters = createSelector(filterStateSelector, (state) => state.toggleFilters.tasks);

const myTaskFilters = createSelector(filterStateSelector, (state) => state.myTasks);
const myTaskToggleFilters = createSelector(filterStateSelector, (state) => state.toggleFilters.myTasks);

const taskOrder = createSelector(eventPreferencesStateSelector, (state) => state.document.task_order);
const user = createSelector(userStateSelector, (state) => state.user);
const userLoggedIn = createSelector(userStateSelector, (state) => state.loggedIn);

const event = createSelector(eventStateSelector, (state) => state.event);
const calendarDate = createSelector(tasksStateSelector, (state) => state.calendarDate);

export const tasksSelector = createSelector(
  tasksDict,
  (tasksDict: Dictionary<Task>) => Object.values(tasksDict)
)

export const unseenTasksCount = createSelector(
  tasksSelector,
  user,
  (tasks: Task[], user: User) => tasks.map(t => t.seen && t.seen[user.uid] ? 0 : 1).reduce((a, v) => a + v, 0)
)

const sortTasksInPlaceByPreference = (tasks: Task[], taskOrder: string[] = []): Task[] => tasks.sort((a, b) => {
  const ai = taskOrder.indexOf(a.id);
  const bi = taskOrder.indexOf(b.id);
  if (ai > bi) {
    return 1;
  } else if (ai < bi) {
    return -1;
  }
  if (a.createdAt > b.createdAt) {
    return -1;
  } else if (a.createdAt < b.createdAt) {
    return 1;
  } else {
    return 0;
  }
})



export const filterTasks = (tasks: Task[], filters: FilterGroup, toggleFilters: IToggleFilter, userYou: User): Task[] => tasks.filter(t => {
  const createdByMe = filters.me && t.createdBy === userYou.uid
  const flagged = t.flag// && filters.flagged && t.flag[filters.flagged]
  const myResponsibility = filters.myResponsibility && t.responsibility === userYou.email
  const anyMeta = createdByMe || flagged || myResponsibility
  return ![
    () => !toggleFilters.sortByDate || t.dueDate,
    () => !toggleFilters.hideCompleted || t.status !== 'complete',
    // () => !filters.me || (t.createdBy === userYou.uid && !t.responsibility) || t.responsibility === userYou.email, <== Old "me filter"
  /*   () => !filters.me || t.createdBy === userYou.uid,
    () => !filters.myResponsibility || t.responsibility === userYou.email,
    () => !filters.flagged || (t.flag && t.flag[filters.flagged]), */
    () => !filters.me || anyMeta,
    () => !filters.myResponsibility || anyMeta,
    () => !filters.flagged || anyMeta,
    () => !filters.entityIds || filters.entityIds.includes(t.entityId),
    () => !filters.responsibilities || myResponsibility || filters.responsibilities.includes(t.responsibility),
    () => !filters.createdBy || createdByMe || filters.createdBy.includes(t.createdBy),
    () => !filters.visibilities || (t.visibility && t.visibility.some(v => filters.visibilities.includes(v))),
    () => !filters.groupVisibilities || (t.groupVisibility && t.groupVisibility.some(v => filters.groupVisibilities.includes(v))),
    () => !filters.programs || filters.programs.includes(t.program),
    () => !filters.taskLabels || (t.labels && t.labels.some(v => filters.taskLabels.includes(v))),
  ].some(passesFilterFunction => !passesFilterFunction()) 
})

const getTasksInfo = (filters, toggleFilters) => createSelector(
  user,
  userLoggedIn,
  filters,
  toggleFilters,
  (user: User, loggedIn: boolean, filters: FilterGroup, toggleFilters: IToggleFilter) => ({ user, loggedIn, filters, toggleFilters })
)

const getFilteredTasks = tasksPreselector => createSelector(
  tasksPreselector,
  tasksSelector,
  taskOrder,
  ({ user, loggedIn, filters, toggleFilters }: {
    user: User, loggedIn: boolean, filters: FilterGroup, toggleFilters: IToggleFilter
  },
    tasks: Task[],
    taskOrder: string[]
  ): Task[] => {

    if (!tasks.length || !loggedIn) {
      return []
    }
    if (filters.search) {
      const searchTerm = filters.search.toLocaleLowerCase()
      const searchableFields = ['name', 'content', 'program' ]
      tasks = tasks.filter(task => searchableFields.some(field => task[field] && task[field].toLocaleLowerCase().includes(searchTerm)))
    }
    // console.log({ tasks: jsonCopyObject(tasks) })
    if (!filters.count && !toggleFilters.sortByDate && !toggleFilters.hideCompleted) { // sort by date and hide completed are filters, but don't want to include them in count for ui reasons
      sortTasksInPlaceByPreference(tasks, taskOrder)
      return tasks
    }

    // console.log(jsonCopyObject({ tasks, filters, toggleFilters, user }))
    tasks = filterTasks(tasks, filters, toggleFilters, user)
    // console.log({ tasks: jsonCopyObject(tasks) })

    if (!tasks.length) {
      return []
    }

    if (toggleFilters.sortByDate) {
      tasks.sort((a, b) => a.dueDate < b.dueDate ? -1 : 1)
      let currentDate
      tasks.forEach(t => {
        if (currentDate && t.date > currentDate && t.date > new Date()) {
          t.showHeader = true
        } else {
          t.showHeader = false
        }
        currentDate = t.date
      })
      tasks[0].showHeader = true

    } else {
      sortTasksInPlaceByPreference(tasks, taskOrder)
    }
    // console.log({ tasks: jsonCopyObject(tasks) })
    return tasks

  }
)

const getEventsFilteredTasks = (filteredEventsReselector, filteredTasksReselector) => createSelector(
  filteredEventsReselector,
  filteredTasksReselector,
  ({ eventIds }: { eventIds: Set<string> }, tasks: Task[]): Task[] => {
    return tasks.filter(t => eventIds.has(t.eventId))
  }
)

const sidebarTasksPreselector = getTasksInfo(sidebarTaskFilters, sidebarToggleFilters)
const tableTasksPreselector = getTasksInfo(tableTaskFilters, tableToggleFilters)
const mainTasksPreselector = getTasksInfo(mainpageTaskFilters, mainpageToggleFilters)
const calendarTasksPreselector = getTasksInfo(calendarTaskFilters, calendarToggleFilters)
const myTasksPreselector = getTasksInfo(myTaskFilters, myTaskToggleFilters)
const allTasksCalendarPreselector = getTasksInfo(allTasksCalendarFilters, allTasksCalendarToggleFilters)

export const getFilteredSidebarTasks = getFilteredTasks(sidebarTasksPreselector)
export const getFilteredPageTasks = getFilteredTasks(mainTasksPreselector)
export const getFilteredTableTasks = getFilteredTasks(tableTasksPreselector)
export const getFilteredCalendarTasks = getFilteredTasks(calendarTasksPreselector)
export const getFilteredAllTasksCalendarTasks = getEventsFilteredTasks(filteredCalendarEventsReselectorGenerator(filterPageTypes.allTasksCalendar), getFilteredTasks(allTasksCalendarPreselector))
export const getFilteredMyTasks = getFilteredTasks(myTasksPreselector)

export const getFilteredCalendarDataFrom = filteredCalendarTasks => createSelector(
  filteredCalendarTasks,
  (tasks: Task[]): Dictionary<Task[]> => {
    
    const tasksByDate: Dictionary<Task[]> = {}
    tasks.filter(t => t.dueDate).forEach(task => {
      if (!tasksByDate[task.dueDate]) tasksByDate[task.dueDate] = []
      tasksByDate[task.dueDate].push(task)
    })

    Object.keys(tasksByDate).forEach(dateId => tasksByDate[dateId].sort(sortByProp('name')))
    // console.log({ tasks, tasksByDate })
    return tasksByDate
  }
)

export const getFilteredCalenderData = getFilteredCalendarDataFrom(getFilteredCalendarTasks)
export const getFilteredCalenderMyTaskData = getFilteredCalendarDataFrom(getFilteredMyTasks)
export const getFilteredAllTasksCalendarReselector = getFilteredCalendarDataFrom(getFilteredAllTasksCalendarTasks)

export const reselectorsByCalendarTaskType = {
  [filterPageTypes.tasksCalendar]: getFilteredCalenderData,
  [filterPageTypes.allTasksCalendar]: getFilteredAllTasksCalendarReselector,
  [filterPageTypes.myTasks]: getFilteredCalenderMyTaskData,
}

export const getFilteredTasksByType = {
  [filterPageTypes.tasksSidebar]: getFilteredSidebarTasks,
  [filterPageTypes.tasks]: getFilteredPageTasks,
  [filterPageTypes.tasksCalendar]: getFilteredCalendarTasks,
  [filterPageTypes.allTasksCalendar]: getFilteredAllTasksCalendarTasks,
  [filterPageTypes.tasksTable]: getFilteredTableTasks,
}
